import { makeVar, useReactiveVar } from '@apollo/client';
import { Stack, useTheme } from '@fluentui/react';
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  BackgroundVariant,
  Edge,
  Node,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import clsx from 'clsx';
import { DRAWER_CLOSE_WIDTH, DRAWER_OPEN_WIDTH } from 'common/constants';
import { getWindowDimensions } from 'common/utils/columnUtilities';
import React, { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { ApprovalSetupCommonData } from 'settings/account/approvalSetup/__generated__/ApprovalSetupCommonData';
import { FlowApproval_Query } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/FlowApproval';
import { expandedNav } from 'utility/cache/ui';
import {
  CustomReactFlowControls,
  setInteractive,
} from '../../../../../../common/components/CustomReactFlowControls';
import {
  APPROVAL_BRANCH_INITIAL_VALUES,
  APPROVAL_LEVELS_INITIAL_VALUES,
  LEVEL_NODE_WIDTH,
} from '../../constants';
import { useStyles } from '../../index.styles';
import { ApprovalValues } from '../../types';
import { setNodeScrollState } from '../Conditions';
import { AddApprovalBranchNode } from '../Node/AddApprovalBranchNode';
import { AddLevelNode } from '../Node/AddLevelNode';
import { ApprovalBranchNode } from '../Node/ApprovalBranchNode';
import { ApproverLevelEdge } from '../Node/common/ApproverLevelEdge';
import { CloseEdge } from '../Node/common/CloseEdge';
import { ConditionNode } from '../Node/ConditionNode';
import { LevelNode } from '../Node/LevelNode';
import TextUpdaterNode from '../Node/TextUpdater';
import { TitleNode } from '../Node/TitleNode';
import {
  getClosestEdgeUtil,
  getUpdatedEdgesWithReparenting,
} from '../utils/utils';
import { convertToApprovalLevelNodes } from './utils';
import { ApprovalSetupFlowCommonData } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/ApprovalSetupFlowCommonData';

const edgeTypes = {
  'approver-level-custom-edge': ApproverLevelEdge,
  'close-edge': CloseEdge,
};
export const movingNodeId = makeVar<string>('');

const nodeTypes = {
  AddLevelNode,
  AddApprovalBranchNode,
  TitleNode,
  LevelNode,
  ApprovalBranchNode,
  TextUpdaterNode,
  ConditionNode,
};

type ApproverLevelsProps = {
  approvalDetailsLoading: boolean;
  approvalDetailsData: FlowApproval_Query | undefined;
  approvalTypes: ApprovalSetupFlowCommonData | undefined;
};

const ApproverLevelsForm: React.FC<ApproverLevelsProps> = (props) => {
  const styles = useStyles();

  const { getNodes, getEdges, getInternalNode, setViewport } = useReactFlow();
  const [nodes, setNodes] = useNodesState<Node>(getNodes());
  const [edges, setEdges] = useEdgesState<Edge>([]);
  const isNodeScrollActive = useReactiveVar(setNodeScrollState);
  const theme = useTheme();
  const { watch, setValue } = useFormContext<ApprovalValues>();
  const approvalTreesFormData = watch('approvalTrees');
  let approvalTreeParentNodeId: string | undefined;
  const isInteractive = useReactiveVar(setInteractive);
  const renderRef = useRef(false);
  const onNodesChange = useCallback((changes: any) => {
    const updated = applyNodeChanges(changes, getNodes());
    setNodes(updated);
  }, []);

  const onEdgesChange = useCallback((changes: any) => {
    const updated = applyEdgeChanges(changes, getEdges());
    setEdges(updated);
  }, []);

  const onConnect = useCallback((params: any) => {
    const updated = addEdge(params, getEdges());
    setEdges(updated);
  }, []);

  const reactFlowStyles: React.CSSProperties | undefined = {
    backgroundColor: theme.palette.white,
  };

  const expanded = useReactiveVar(expandedNav);

  useEffect(() => {
    setNodeScrollState(true);
  }, []);
  const { width } = getWindowDimensions();

  useEffect(() => {
    if (approvalTreesFormData && props.approvalTypes) {
      const { nodes: modulatedNodes, edges: modulatedEdges } =
        convertToApprovalLevelNodes(
          approvalTreesFormData,
          'vertical',
          props.approvalDetailsData?.approval?._isUpdatable ?? true,
          props.approvalDetailsData?.approval?._isDeletable ?? true
        );
      setNodes([...modulatedNodes]);
      setEdges([...modulatedEdges]);
      if (!renderRef.current && width) {
        setViewport({
          x: width / 2 - LEVEL_NODE_WIDTH,
          y: 80,
          zoom: 0.8,
        });
        renderRef.current = true;
      }
    }
  }, [approvalTreesFormData, props.approvalTypes, setNodes, setEdges]);

  const getClosestEdge = useCallback((node: any) => {
    const internalNode = getInternalNode(node.id); // your logic
    const existingNodes = getNodes(); // your logic
    return getClosestEdgeUtil(internalNode, existingNodes);
  }, []);

  const onNodeDrag = useCallback(
    (_: any, node: any) => {
      movingNodeId(node.data.id);
      const closeEdge = getClosestEdge(node);
      setEdges((es) => {
        const nextEdges = es.filter((e) => e.className !== 'temp');

        if (
          closeEdge &&
          !nextEdges.find(
            (ne) =>
              ne.source === closeEdge.source && ne.target === closeEdge.target
          )
        ) {
          closeEdge.className = 'temp';
          nextEdges.push(closeEdge);
        }

        return nextEdges;
      });
    },
    [getClosestEdge, setEdges]
  );
  function adjustEdges(
    edges: Edge[],
    newEdge: {
      id?: string;
      source: string;
      target: string;
      className?: string;
      type?: string;
      sourceHandle?: string;
      targetHandle?: string;
      animated?: boolean;
      label?: string;
    },
    nodeType: string | undefined,
    nodeId: string
  ): Edge[] {
    const updatedEdges = getUpdatedEdgesWithReparenting(edges, newEdge);

    let conditionIndex = 1;
    const currentNodes = getNodes();
    const sortedByY = currentNodes.sort((a, b) => a.position.y - b.position.y);
    const approvalParentBranchId = getParentApprovalFlowId(nodeId, sortedByY);
    const updatedNodes = sortedByY.map((node) => {
      if (node.type === 'LevelNode' && nodeType === 'LevelNode') {
        const updatedNode = {
          ...node,
          data: {
            ...node.data,
            approvalLevel: conditionIndex,
            levelIndex: conditionIndex,
          },
        };
        conditionIndex++;
        return updatedNode;
      } else if (
        node.type === 'ApprovalBranchNode' &&
        nodeType === 'ApprovalBranchNode'
      ) {
        const updatedNode = {
          ...node,
          data: {
            ...node.data,
            branchOrder: conditionIndex,
          },
        };
        conditionIndex++;
        return updatedNode;
      }
      return node;
    });

    const ApprovalTreeArray = updatedNodes
      .filter((node) => node.type === 'LevelNode')
      .map((node) => node.data);

    if (nodeType === 'LevelNode') {
      setValue(
        'approvalTrees',
        // @ts-ignore
        [...ApprovalTreeArray, { ...APPROVAL_LEVELS_INITIAL_VALUES }],
        { shouldDirty: true }
      );
    } else {
      const ApprovalBranchesArray = updatedNodes
        .filter(
          (node) =>
            node.type === nodeType &&
            node.id.match(/^\d+-level/)?.[0] === approvalTreeParentNodeId
        )
        .map((node) => node.data);

      const updatedApprovalTreeArray = ApprovalTreeArray.map((tree) => {
        // @ts-ignore
        if (tree.id === approvalParentBranchId) {
          return {
            ...tree,
            approvalBranches: [
              ...ApprovalBranchesArray,
              // @ts-ignore
              APPROVAL_BRANCH_INITIAL_VALUES,
            ],
          };
        }
        return tree;
      });

      setValue(
        'approvalTrees',
        // @ts-ignore
        [...updatedApprovalTreeArray, { ...APPROVAL_LEVELS_INITIAL_VALUES }],
        { shouldDirty: true }
      );
    }

    return updatedEdges;
  }

  function getParentApprovalFlowId(
    branchId: string,
    nodes: any[]
  ): string | null {
    const match = branchId.match(/^\d+-level/);
    approvalTreeParentNodeId = match?.[0];
    const parentNode = nodes.find((node) => node.id === match?.[0]);
    return parentNode?.data?.id ?? null;
  }
  const onNodeDragStop = useCallback(
    (_: any, node: any) => {
      movingNodeId(undefined);
      const closeEdge = getClosestEdge(node);
      setEdges((es) => {
        const nextEdges = es.filter((e) => e.className !== 'temp');
        if (
          closeEdge &&
          !nextEdges.find(
            (ne) =>
              ne.source === closeEdge.source && ne.target === closeEdge.target
          )
        ) {
          // Remove the temporary edge if it exists
          const filteredEdges = nextEdges.filter(
            (e) =>
              !(e.source === closeEdge.source && e.target === closeEdge.target)
          );
          // Apply the edge reassignment algorithm

          const updatedEdges = adjustEdges(
            filteredEdges,
            closeEdge,
            node.type,
            node.id
          );
          setEdges(updatedEdges);
          //  onLayout('TB', getNodes(), updatedEdges);
          return updatedEdges;
        }
        setEdges(nextEdges);
        //  onLayout('TB', getNodes(), nextEdges);
        return nextEdges;
      });
    },
    [getClosestEdge]
  );

  return (
    <Stack
      className={clsx(styles.bodyContainer, styles.bodyContainerAlternative)}
    >
      <div
        style={{
          width: `calc(100% - ${
            expanded ? DRAWER_CLOSE_WIDTH : DRAWER_OPEN_WIDTH
          })`,
          height: 'calc(100vh - 100px)',
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onConnect={onConnect}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          style={reactFlowStyles}
          panOnScroll={isNodeScrollActive}
          panOnDrag={isNodeScrollActive || isInteractive}
          nodesDraggable={isInteractive}
          nodesConnectable={isInteractive}
          elementsSelectable={isInteractive}
          zoomOnScroll={isInteractive}
          zoomOnPinch={isInteractive}
          zoomOnDoubleClick={isInteractive}
          edgeTypes={edgeTypes}
        >
          <CustomReactFlowControls />
          <Background
            variant={BackgroundVariant.Dots}
            gap={12}
            size={1}
            color={`${theme.palette.neutralDark}66`}
          />
        </ReactFlow>
      </div>
    </Stack>
  );
};

export const ApproverLevels: React.FC<ApproverLevelsProps> = (props) => {
  return (
    <ReactFlowProvider>
      <ApproverLevelsForm {...props} />
    </ReactFlowProvider>
  );
};
